import { graphql } from "@acto/gql";
import { PaginatedDataAccessResult, PagingOptions } from "@acto/taskpane/core/data-access/types";
import { useMemo } from "react";
import { ClientSearchMode, TodoStatus as TodoStatusNode, TodoType as TodoTypeNode } from "@acto/gql/graphql";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { Todo } from "@acto/taskpane/core/domain";

const TodosQuery = graphql(`
  query GetTodos($first: Int!, $after: String, $filter: TodoFilter) {
    todos(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        node {
          id
          __typename
          title
          description
          dueDate
          processedStatus
          processedReason
          isWorkflow
          isNoteBased
          client {
            id
            legacyId
            __typename
            name
            externalReference
          }
          assignedUser {
            id
            name
            __typename
            employee {
              id
              name
              __typename
            }
          }
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface TodoFilterOptions {
  clientId?: string;
  /** Smart client search: name, city, postcode and customer number (typo-tolerant). */
  clientSearchTerm?: string;
  todoTypes?: TodoTypeNode[];
  dueDate?: string;
  showOverdue?: boolean;
  noteId?: string;
  processedStatus?: TodoStatusNode;
}

export { TodoTypeNode as TodoType };

export function useTodos({
  clientId,
  clientSearchTerm,
  todoTypes,
  dueDate,
  showOverdue,
  noteId,
  processedStatus = TodoStatusNode.Open,
  limit = 10,
  enabled = true,
}: PagingOptions & TodoFilterOptions & { enabled?: boolean }): PaginatedDataAccessResult<Todo> {
  const filter = useMemo(() => {
    const baseFilter = {
      processedStatus,
      todoTypes: todoTypes && todoTypes.length > 0 ? todoTypes : undefined,
      dueDate,
      showOverdue,
      noteId,
      clientId,
    };

    if (clientSearchTerm && clientSearchTerm.length > 0) {
      return {
        ...baseFilter,
        clientSearchTerm,
        clientSearchMode: ClientSearchMode.Smart,
      };
    } else return baseFilter;
  }, [clientId, clientSearchTerm, todoTypes, dueDate, showOverdue, noteId, processedStatus]);

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter, refetch } = usePagination({
    query: TodosQuery,
    variables: { filter },
    limit,
    enabled,
    connection: (data) => data.todos,
  });

  const todos = useMemo(() => data?.map(Todo.fromGraphql), [data]);

  return {
    data: todos,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
  };
}
